import { getOrDefault } from 'libs/PagingParser'
import * as R from 'ramda'

import {
  NOTI_PRIORITY_HIGH,
  NOTI_PRIORITY_LOW,
  NOTI_PRIORITY_URGENT,
  NOTI_PRIORITY_EMERGENCY,
} from 'app/constants/priority'
import { mediaType } from 'app/components/media/media'
import { TYPE_EVENT } from 'app/constants/messageType'
import { removeDoNotTranslateStyle } from 'commons/form'
import { MESSAGE_EXPIRATION_NONE } from '../expiration/messageExpiration'

// When it's initial notification the media object (image, pdf) for submit will be {}
// When it's initial notification the media object (video) for submit will be {media: [mediaObject]}
// When notification has media the media object for submit will be {media: [mediaObject]}
// When notification has no media the media object for submit will be {}

const newOrderImage = (files) => {
  const fileDisplayReOrder = files.map((item, index) => {
    return { ...item, sortIndex: index }
  })
  return fileDisplayReOrder
}

const mapFileForUpdate = R.compose(
  R.mergeRight({ type: mediaType.image }),
  R.objOf('media'),
  newOrderImage,
  R.filter((item) => {
    return item.isSuccess && R.isNil(item.error) && !item.isCancel
  }),
  R.path(['media', 'submit', 'file']),
)

const mapFilePdfForUpdate = R.compose(
  R.ifElse(
    R.length,
    R.compose(
      R.mergeRight({ type: mediaType.pdf }),
      R.objOf('media'),
      R.map((item) => {
        return { file: item.url, fileName: item.file.name }
      }),
    ),
    R.always({}),
  ),
  R.filter((item) => {
    return (
      item.isSuccess &&
      !R.has('id')(item) &&
      R.isNil(item.error) &&
      !item.isCancel
    )
  }),
  R.path(['media', 'submit', 'file']),
)

const mapVideoForUpdate = R.compose(
  R.ifElse(
    R.length,
    R.compose(
      R.mergeRight({ type: mediaType.video }),
      R.objOf('media'),
      R.map((item) => {
        return { url: item.url }
      }),
    ),
    R.always({}),
  ),
  R.filter((item) => {
    return (
      item.isSuccess &&
      !R.has('id')(item) &&
      R.isNil(item.error) &&
      !item.isCancel
    )
  }),
  R.path(['media', 'submit', 'file']),
)

const getMediaAndReOrder = R.cond([
  [R.pathEq(['media'], undefined), R.always({})],
  [
    R.pathEq(['media', 'preview', 'type'], mediaType.image),
    R.ifElse(
      R.compose(R.not, R.isNil, R.path(['media', 'submit'])),
      mapFileForUpdate,
      R.always({}),
    ),
  ],
  [
    R.pathEq(['media', 'preview', 'type'], mediaType.pdf),
    R.ifElse(
      R.compose(R.not, R.isNil, R.path(['media', 'submit'])),
      mapFilePdfForUpdate,
      R.always({}),
    ),
  ],
  [
    R.pathEq(['media', 'preview', 'type'], mediaType.video),
    R.ifElse(
      R.compose(R.not, R.isNil, R.path(['media', 'submit'])),
      mapVideoForUpdate,
      R.always({}),
    ),
  ],
])

const getDeletedMediaIds = R.o(
  R.ifElse(
    R.isNil,
    R.always({}),
    R.o(R.objOf('deletedMediaIds'), R.path(['submit', 'deletedMediaIds'])),
  ),
  R.prop('media'),
)

const getSortIndex = R.ifElse(
  R.isEmpty,
  R.always({}),
  R.compose(
    R.ifElse(
      R.length,
      R.compose(
        R.objOf('updatedSortIndex'),
        R.when(R.isEmpty, R.always({})),
        R.map((item) => {
          return { mediaId: item.id, sortIndex: item.sortIndex }
        }),
      ),
      R.always({}),
    ),
    R.filter(R.has('id')),
    R.prop('media'),
  ),
)

const getMediaForSubmit = (fileObject) => {
  if (R.includes(fileObject.type, [mediaType.pdf, mediaType.video])) {
    return R.ifElse(R.isEmpty, R.always({}), R.pick(['media']))(fileObject)
  }

  return R.ifElse(
    R.isEmpty,
    R.always({}),
    R.compose(
      R.ifElse(
        R.length,
        R.compose(
          R.objOf('media'),
          R.when(R.isEmpty, R.always({})),
          R.map((item) => {
            return { ...item.fileSubmit, sortIndex: item.sortIndex }
          }),
        ),
        R.always({}),
      ),
      R.filter((item) => {
        return (
          item.isSuccess &&
          !R.has('id')(item) &&
          R.isNil(item.error) &&
          !item.isCancel
        )
      }),
      R.prop('media'),
    ),
  )(fileObject)
}

const priorityToApi = (priority) => {
  switch (priority) {
    case NOTI_PRIORITY_HIGH.label:
      return NOTI_PRIORITY_HIGH.id
    case NOTI_PRIORITY_LOW.label:
      return NOTI_PRIORITY_LOW.id
    case NOTI_PRIORITY_URGENT.label:
      return NOTI_PRIORITY_URGENT.id
    case NOTI_PRIORITY_EMERGENCY.label:
      return NOTI_PRIORITY_EMERGENCY.id
    default:
      return undefined
  }
}

export const priorityFromApi = (priority) => {
  switch (priority) {
    case NOTI_PRIORITY_LOW.id:
      return NOTI_PRIORITY_LOW.label
    case NOTI_PRIORITY_HIGH.id:
      return NOTI_PRIORITY_HIGH.label
    case NOTI_PRIORITY_URGENT.id:
      return NOTI_PRIORITY_URGENT.label
    case NOTI_PRIORITY_EMERGENCY.id:
      return NOTI_PRIORITY_EMERGENCY.label
    default:
      return undefined
  }
}

export const mapToApi = (formModel) => (translations) => {
  const mediaReOrder = getMediaAndReOrder(formModel)
  const updatedSortIndex = getSortIndex(mediaReOrder)
  const media = getMediaForSubmit(mediaReOrder)
  const deletedMediaIds = getDeletedMediaIds(formModel)

  const eventLocationTimezone =
    TYPE_EVENT === formModel.notiType && formModel.event.timeZoneId

  const socialMediaPages =
    formModel.socialChannels && formModel.socialChannels.pages
      ? formModel.socialChannels.pages
      : []
  const filterItemId = (pages) =>
    R.filter(
      (item) => item.id && !R.includes(item.type, ['twitter', 'linkedin']),
      pages,
    )
  const socials = R.uniq(R.map((item) => item.value, socialMediaPages))

  return {
    type: formModel.notiType,
    priority: priorityToApi(formModel.priority),
    translations: R.map(
      R.over(R.lensProp('body'), removeDoNotTranslateStyle),
      translations,
    ),
    date: getOrDefault(undefined)(() => formModel.scheduleDateTime),
    scheduleTimezone: formModel.scheduleTimezone,
    remindDate: getOrDefault(undefined)(() => formModel.reminderDateTime),
    reminderTimezone: formModel.reminderTimezone,
    address: getOrDefault(undefined)(() => formModel.location),
    latitude: getOrDefault(undefined)(() => formModel.event.lat.toString()),
    longitude: getOrDefault(undefined)(() => formModel.event.lng.toString()),
    startTime: getOrDefault(undefined)(() => formModel.event.startDateTime),
    endTime: getOrDefault(undefined)(() => formModel.event.endDateTime),
    startTimezone: getOrDefault(undefined)(() => formModel.event.startTimezone),
    endTimezone: getOrDefault(undefined)(() => formModel.event.endTimezone),
    timezone:
      TYPE_EVENT === R.prop('notiType', formModel)
        ? eventLocationTimezone
        : R.prop('timezone', formModel),
    allowConversation: formModel.allowConversation || false,
    pageIds: formModel.socialChannels
      ? R.map((item) => item.id, filterItemId(socialMediaPages))
      : [],
    topicIds: R.compose(R.map(R.prop('id')), R.defaultTo([]))(formModel.topics),
    twitter: R.find((item) => item.type === 'twitter', socialMediaPages)
      ? true
      : false,
    linkedin: R.find((item) => item.type === 'linkedin', socialMediaPages)
      ? true
      : false,
    isTtsEnabled: formModel.isTtsEnabled || false,
    isSharingEnabled: formModel.isSharingEnabled || false,
    ...media,
    ...updatedSortIndex,
    ...deletedMediaIds,
    // ...mapRecipientToApi(R.prop(RECIPIENTS, formModel)),
    isActive: getOrDefault(undefined)(() =>
      formModel.isActive ? Number(formModel.isActive) : undefined,
    ),
    voucherId: getOrDefault(undefined)(() =>
      R.path(['voucher', 'id'], formModel),
    ),
    channels: formModel.socialChannels
      ? [...formModel.socialChannels.channels, ...socials]
      : [],
    shortName: formModel.shortName,
    displayHistory: getOrDefault(undefined)(() => formModel.displayHistory),
    expirationTime:
      formModel.expirationTime === MESSAGE_EXPIRATION_NONE
        ? undefined
        : formModel.expirationTime,
    mediaSocials: getOrDefault(undefined)(() =>
      R.map(
        R.pick(['url', 'isSelected', 'socialType', 'type']),
        R.prop('mediaSocials', formModel),
      ),
    ),
  }
}

export const toApiReminderDate = 'remindDate'
export const fromApiReminderDate = 'reminderDate'
